import { Action } from 'redux';
import { Dispatch } from 'react-redux';
import { StoreState } from '../../reducers';
import { detectStaleOfflineDownloadIds } from '../../utils/staleOfflineDownloads';
import {
	getStaleOfflineCleanupConfig,
	isStaleOfflineCleanupEnabled,
} from '../../utils/staleOfflineCleanupConfig';
import Utils from '../../utils';
import UserAgentUtils from '../../utils/userAgent';
import IndexedDBManager from '../../utils/indexedDB/manager';
import { deleteItem } from '../DownloadManager/actions';
import { getOrFetchDriveItemMeta, receiveDriveItems } from '../DriveItemList/actions';
import { DriveItemInfo } from '../DriveItemList/reducer';
import { createNotification } from '../NotificationBar/actions';
import { Api } from '../../typings/api';
import { StaleOfflineCleanupActionKeys } from './typings';

interface SetStaleIdsAction extends Action {
	type: StaleOfflineCleanupActionKeys.SET_STALE_IDS;
	staleIds: number[];
}

interface DismissForSessionAction extends Action {
	type: StaleOfflineCleanupActionKeys.DISMISS_FOR_SESSION;
}

interface SetCleaningUpAction extends Action {
	type: StaleOfflineCleanupActionKeys.SET_CLEANING_UP;
	isCleaningUp: boolean;
}

interface SetAutoCleanEnabledAction extends Action {
	type: StaleOfflineCleanupActionKeys.SET_AUTO_CLEAN_ENABLED;
	autoCleanEnabled: boolean;
}

interface SetPreferencesLoadedAction extends Action {
	type: StaleOfflineCleanupActionKeys.SET_PREFERENCES_LOADED;
	preferencesLoaded: boolean;
}

interface ClearStaleOfflineCleanupAction extends Action {
	type: StaleOfflineCleanupActionKeys.CLEAR;
}

const STALE_CLEANUP_BATCH_SIZE = 3;

const setStaleOfflineDownloadIds = (staleIds: number[]): SetStaleIdsAction => ({
	type: StaleOfflineCleanupActionKeys.SET_STALE_IDS,
	staleIds,
});

const dismissStaleOfflinePopupForSession = (): DismissForSessionAction => ({
	type: StaleOfflineCleanupActionKeys.DISMISS_FOR_SESSION,
});

const setStaleOfflineCleanupInProgress = (isCleaningUp: boolean): SetCleaningUpAction => ({
	type: StaleOfflineCleanupActionKeys.SET_CLEANING_UP,
	isCleaningUp,
});

const setAutoCleanEnabled = (autoCleanEnabled: boolean): SetAutoCleanEnabledAction => ({
	type: StaleOfflineCleanupActionKeys.SET_AUTO_CLEAN_ENABLED,
	autoCleanEnabled,
});

const setStaleOfflinePreferencesLoaded = (preferencesLoaded: boolean): SetPreferencesLoadedAction => ({
	type: StaleOfflineCleanupActionKeys.SET_PREFERENCES_LOADED,
	preferencesLoaded,
});

const clearStaleOfflineDownloads = (): ClearStaleOfflineCleanupAction => ({
	type: StaleOfflineCleanupActionKeys.CLEAR,
});

const initStaleOfflineCleanupPreferences = () =>
	async (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<void> => {
		if (!UserAgentUtils.isMobileApp) {
			dispatch(setStaleOfflinePreferencesLoaded(true));
			return;
		}

		const { galleryInfos: { config } } = getState();

		if (!config) {
			return;
		}

		const storedPreference = await IndexedDBManager.getAutoCleanStaleOffline();
		const autoCleanEnabled = storedPreference !== undefined
			? storedPreference
			: getStaleOfflineCleanupConfig(config).autoCleanDefault;

		dispatch(setAutoCleanEnabled(autoCleanEnabled));
		dispatch(setStaleOfflinePreferencesLoaded(true));
	};

const setAutoCleanStaleOfflineEnabled = (autoCleanEnabled: boolean) =>
	async (dispatch: Dispatch<StoreState>): Promise<void> => {
		dispatch(setAutoCleanEnabled(autoCleanEnabled));

		try {
			await IndexedDBManager.setAutoCleanStaleOffline(autoCleanEnabled);
		} catch (error) {
			console.error('Failed to save auto-clean stale offline preference:', error);
		}
	};

const ensureStaleOfflineCleanupPreferences = () =>
	async (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<void> => {
		const { staleOfflineCleanup: { preferencesLoaded } } = getState();

		if (!preferencesLoaded) {
			await dispatch(initStaleOfflineCleanupPreferences());
		}
	};

const checkStaleOfflineDownloadsAfterSync = () =>
	async (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<void> => {
		if (!UserAgentUtils.isMobileApp || !UserAgentUtils.isOnline()) {
			return;
		}

		const { galleryInfos: { config } } = getState();

		if (!config || !isStaleOfflineCleanupEnabled(config)) {
			return;
		}

		await dispatch(ensureStaleOfflineCleanupPreferences());

		const {
			downloadManager,
			driveList: { driveItems },
			staleOfflineCleanup: { autoCleanEnabled },
		} = getState();

		const sharedDirId = Utils.getSharedDirId(getState().driveList, config);
		const staleIds = detectStaleOfflineDownloadIds(
			downloadManager,
			driveItems,
			config.applicationGallery,
			sharedDirId,
		);

		if (staleIds.length === 0) {
			dispatch(setStaleOfflineDownloadIds([]));
			return;
		}

		dispatch(setStaleOfflineDownloadIds(staleIds));

		if (autoCleanEnabled) {
			await dispatch(cleanupStaleOfflineDownloads());
		}
	};

const markStaleDriveItemsAsNotInGallery = (
	driveItemIds: number[],
) => (dispatch: Dispatch<StoreState>, getState: () => StoreState): void => {
	const { driveList: { driveItems } } = getState();
	const driveItemsToUpdate: { [k: number]: DriveItemInfo } = {};

	driveItemIds.forEach((driveItemId) => {
		const driveItem = driveItems[driveItemId];

		if (driveItem) {
			driveItemsToUpdate[driveItemId] = {
				...driveItem,
				isInGallery: false,
			};
		}
	});

	if (Object.keys(driveItemsToUpdate).length > 0) {
		dispatch(receiveDriveItems(driveItemsToUpdate as { [k: number]: Api.DriveItem }, false));
	}
};

const deleteStaleOfflineItem = (driveItemId: number) =>
	(dispatch: Dispatch<StoreState>, getState: () => StoreState) => {
		const { driveList: { driveItems } } = getState();
		const driveItem = driveItems[driveItemId];

		if (driveItem) {
			return dispatch(deleteItem(driveItem));
		}

		return dispatch(getOrFetchDriveItemMeta(driveItemId, false))
			.then((fetchedDriveItem: {} | Api.DriveItem) => {
				if (fetchedDriveItem && (fetchedDriveItem as Api.DriveItem).id) {
					return dispatch(deleteItem(fetchedDriveItem as Api.DriveItem));
				}

				return undefined;
			});
	};

const cleanupStaleOfflineDownloads = () =>
	async (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<void> => {
		const { staleOfflineCleanup: { staleIds, isCleaningUp }, galleryInfos: { config } } = getState();

		if (!config || !isStaleOfflineCleanupEnabled(config) || isCleaningUp || staleIds.length === 0) {
			return;
		}

		const idsToDelete = staleIds.slice();
		const { showCleanupNotification } = getStaleOfflineCleanupConfig(config);
		dispatch(setStaleOfflineCleanupInProgress(true));
		dispatch(markStaleDriveItemsAsNotInGallery(idsToDelete));

		try {
			for (let index = 0; index < idsToDelete.length; index += STALE_CLEANUP_BATCH_SIZE) {
				const batch = idsToDelete.slice(index, index + STALE_CLEANUP_BATCH_SIZE);
				await Promise.all(batch.map(id => dispatch(deleteStaleOfflineItem(id))));
			}

			if (showCleanupNotification) {
				dispatch(createNotification(
					idsToDelete.length === 1
						? 'info.stale_offline_item_cleanup_complete'
						: 'info.stale_offline_cleanup_complete',
					{
						count: idsToDelete.length,
					},
				));
			}

			dispatch(clearStaleOfflineDownloads());
		} catch (error) {
			console.error('Error cleaning up stale offline downloads:', error);
		} finally {
			dispatch(setStaleOfflineCleanupInProgress(false));
		}
	};

export {
	checkStaleOfflineDownloadsAfterSync,
	dismissStaleOfflinePopupForSession,
	cleanupStaleOfflineDownloads,
	initStaleOfflineCleanupPreferences,
	setAutoCleanStaleOfflineEnabled,
};



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/containers/StaleOfflineCleanup/actions.ts